import * as React from "react";
import { Build, Job } from "~/components/build/Show/lib/types";
import Database from "~/lib/Database";
import { LimitingJob } from "~/components/job/Job/shared/types";
import { JobRow } from "~/components/job/Job/shared/JobRow";
import { LimitingBanner } from "~/components/job/Job/shared/LimitingBanner";
import { useLimitingJobsSubscription } from "~/components/job/Job/shared/useLimitingJobsSubscription";

// If this number of limiting jobs is returned, assume there may be more that were not returned
// To change this limit, first update the value in React::JobPresenter
const LIMITING_JOBS_LIMIT = 30;

export function LimitedByConcurrencyGroup({
  concurrencyGroup,
  concurrency,
  build,
  job,
}: {
  concurrencyGroup: string;
  concurrency: number;
  build: Build;
  job: Job;
}) {
  const [limitingJobs, setLimitingJobs] = React.useState<Array<LimitingJob | null>>([]);

  const getLimitingJobs = React.useCallback(() => {
    fetch(job.limitingJobsPath)
      .then((response) => response.json())
      .then((data) => {
        const limitingJobs = Database.parse(data);
        setLimitingJobs(limitingJobs);
      });
  }, [build.account.slug, build.project.slug, build.id, job.id]);

  React.useEffect(() => {
    getLimitingJobs();
  }, [getLimitingJobs]);

  useLimitingJobsSubscription(limitingJobs, getLimitingJobs, ["limited"]);

  if (limitingJobs.length === 0) {
    return null;
  }

  return (
    <LimitingBanner colorScheme="orange">
      <h3 className="mb-2 mt-0 text-base">Limited by concurrency group</h3>

      <p className="mb-3 mt-0">
        This job is limited by the{" "}
        <span className="monospace border-gray-300 text-xs">{concurrencyGroup}</span> concurrency
        group with a concurrency of {concurrency}. The following jobs are ahead in the group:
      </p>

      <ul className="depth mb-3 max-w-3xl divide-y rounded-sm">
        {limitingJobs.map((job, index) => (
          <JobRow key={job?.uuid || index} job={job} />
        ))}

        {limitingJobs.length >= LIMITING_JOBS_LIMIT && (
          <li key={null} className="monospace m-0 border-gray-300 p-2.5 text-xs">
            Displaying only the first {limitingJobs.length} jobs
          </li>
        )}
      </ul>

      <p className="m-0">
        For more information, see the{" "}
        <a
          href="/docs/pipelines/controlling-concurrency"
          className="text-inherit underline"
          target="_blank"
        >
          documentation
        </a>
        .
      </p>
    </LimitingBanner>
  );
}
